import React, { useEffect, useRef, useState } from "react";
import { X, Check } from "lucide-react";


function AudioTrimmerStep({ song, audioRef, audioDuration, trimStart, setTrimStart, trimEnd, setTrimEnd, setAudioDuration }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [localDuration, setLocalDuration] = useState(audioDuration || 0);
  const timelineRef = useRef(null);

  const CLIP_DURATION = 15;

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      const dur = audio.duration || 0;
      setLocalDuration(dur);
      if (setAudioDuration) setAudioDuration(dur);
      if (trimEnd <= trimStart) {
        setTrimStart(0);
        setTrimEnd(Math.min(CLIP_DURATION, dur));
      }
    };

    if (audio.duration && audio.duration > 0) {
      handleLoadedMetadata();
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleLoadedMetadata);

    audio.load();

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleLoadedMetadata);
    };
  }, [audioRef, setAudioDuration, setTrimStart, setTrimEnd, trimStart, trimEnd]);

  const duration = localDuration > 0 ? localDuration : 180;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.currentTime = trimStart;
      audio.play();
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      if (audio.currentTime >= trimEnd) {
        audio.pause();
        setIsPlaying(false);
      }
    };

    const handlePause = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('play', handlePlay);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('play', handlePlay);
    };
  }, [audioRef, trimEnd]);

  const handleDragStart = (e) => {
    e.preventDefault();
    setIsDragging(true);

    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
      setIsPlaying(false);
    }

    updatePositionFromEvent(e);
  };

  const updatePositionFromEvent = (e) => {
    if (!timelineRef.current || duration <= 0) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const position = percentage * duration;

    const halfClip = CLIP_DURATION / 2;
    let newStart = position - halfClip;

    const maxStart = Math.max(0, duration - CLIP_DURATION);
    newStart = Math.max(0, Math.min(maxStart, newStart));

    setTrimStart(newStart);
    setTrimEnd(Math.min(newStart + CLIP_DURATION, duration));
  };

  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => {
        if (!timelineRef.current || duration <= 0) return;

        const rect = timelineRef.current.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        const position = percentage * duration;

        const halfClip = CLIP_DURATION / 2;
        let newStart = position - halfClip;

        const maxStart = Math.max(0, duration - CLIP_DURATION);
        newStart = Math.max(0, Math.min(maxStart, newStart));

        setTrimStart(newStart);
        setTrimEnd(Math.min(newStart + CLIP_DURATION, duration));
      };
      const handleEnd = () => setIsDragging(false);

      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleEnd);

      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, duration, setTrimStart, setTrimEnd]);

  const selectionWidthPercent = Math.min(100, (CLIP_DURATION / duration) * 100);
  const selectionLeftPercent = (trimStart / duration) * 100;

  return (
    <div className="wizard-step-content">
      <p className="wizard-step-desc">Drag the selection to choose a 15-second clip from your song.</p>

      <div className="audio-trimmer">
        <audio ref={audioRef} src={song?.audio_url} preload="metadata" />

        <div className="trim-info">
          <span>{song?.title || 'Untitled'}</span>
          <span className="subtle">Duration: {formatTime(duration)}</span>
        </div>

        <div className="trim-time-labels">
          <span>{formatTime(trimStart)}</span>
          <span className="trim-duration-badge">{CLIP_DURATION}s</span>
          <span>{formatTime(trimEnd)}</span>
        </div>

        <div
          ref={timelineRef}
          className="trim-timeline-draggable"
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
        >
          <div className="trim-track-full">
            <div className="trim-track-markers">
              {[0, 25, 50, 75, 100].map(pct => (
                <div key={pct} className="trim-marker" style={{ left: `${pct}%` }}>
                  <span>{formatTime((pct / 100) * duration)}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`trim-selection ${isDragging ? 'dragging' : ''}`}
            style={{
              left: `${selectionLeftPercent}%`,
              width: `${selectionWidthPercent}%`
            }}
          >
            <div className="trim-handle trim-handle-left" />
            <div className="trim-selection-inner" />
            <div className="trim-handle trim-handle-right" />
          </div>
        </div>

        <div className="trim-play-container">
          <button
            className="trim-play-btn"
            onClick={togglePlayPause}
            data-testid="trim-play-pause"
          >
            {isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1"></rect>
                <rect x="14" y="4" width="4" height="16" rx="1"></rect>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function VideoGenerationWizard({ song, step, setStep, photo, setPhoto, scene, setScene, trimStart, setTrimStart, trimEnd, setTrimEnd, generating, onGenerate, onClose, scenes }) {
  const fileInputRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioPreviewRef = useRef(null);

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      window.alert('Please select a JPG or PNG image');
      return;
    }

    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (song?.audio_url && audioPreviewRef.current) {
      const audio = audioPreviewRef.current;
      const handleLoaded = () => {
        setAudioDuration(audio.duration || 60);
        setTrimStart(0);
        setTrimEnd(Math.min(15, audio.duration || 15));
      };
      audio.addEventListener('loadedmetadata', handleLoaded);
      return () => audio.removeEventListener('loadedmetadata', handleLoaded);
    }
  }, [song?.audio_url, setTrimStart, setTrimEnd]);

  const canProceed = () => {
    if (step === 1) return !!photo;
    if (step === 2) return !!scene;
    if (step === 3) return trimEnd - trimStart >= 5 && trimEnd - trimStart <= 15;
    return false;
  };

  return (
    <div className="video-wizard-overlay" data-testid="video-wizard">
      <div className="video-wizard">
        <div className="video-wizard-header">
          <h2>Generate Video</h2>
          <button className="btn-icon" onClick={onClose} data-testid="video-wizard-close">
            <X size={20} />
          </button>
        </div>

        <div className="video-wizard-steps">
          <div className={`wizard-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'done' : ''}`}>
            <div className="step-number">1</div>
            <span>Upload Photo</span>
          </div>
          <div className="step-connector" />
          <div className={`wizard-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'done' : ''}`}>
            <div className="step-number">2</div>
            <span>Select Scene</span>
          </div>
          <div className="step-connector" />
          <div className={`wizard-step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <span>Trim Audio</span>
          </div>
        </div>

        <div className="video-wizard-content">
          {step === 1 && (
            <div className="wizard-step-content">
              <p className="wizard-step-desc">Upload a portrait photo of yourself or someone else. This will be transformed to match your chosen scene style.</p>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoSelect}
                accept="image/jpeg,image/jpg,image/png"
                style={{ display: 'none' }}
              />

              {!photoPreview ? (
                <div
                  className="photo-upload-area"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="photo-upload-area"
                >
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="upload-icon-svg">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <span>Click to upload photo</span>
                  <span className="upload-hint">JPG, JPEG, or PNG</span>
                </div>
              ) : (
                <div className="photo-preview-container">
                  <img src={photoPreview} alt="Preview" className="photo-preview" />
                  <button
                    className="btn-outline"
                    onClick={() => { setPhoto(null); setPhotoPreview(null); fileInputRef.current.value = ''; }}
                  >
                    Change Photo
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="wizard-step-content">
              <p className="wizard-step-desc">Choose a scene style for your video. Your photo will be transformed to match this aesthetic.</p>

              <div className="scene-grid">
                {scenes.map(s => (
                  <div
                    key={s.id}
                    className={`scene-card ${scene === s.id ? 'scene-selected' : ''}`}
                    onClick={() => setScene(s.id)}
                    data-testid={`scene-${s.id}`}
                  >
                    <img src={s.image} alt={s.name} />
                    <span className="scene-name">{s.name}</span>
                    {scene === s.id && <div className="scene-check"><Check size={16} /></div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <AudioTrimmerStep
              song={song}
              audioRef={audioPreviewRef}
              audioDuration={audioDuration}
              setAudioDuration={setAudioDuration}
              trimStart={trimStart}
              setTrimStart={setTrimStart}
              trimEnd={trimEnd}
              setTrimEnd={setTrimEnd}
            />
          )}
        </div>

        <div className="video-wizard-footer">
          {step > 1 && (
            <button className="btn-outline" onClick={() => setStep(step - 1)} disabled={generating}>
              Back
            </button>
          )}
          <div className="flex-1" />
          {step < 3 ? (
            <button
              className="btn-primary"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              data-testid="wizard-next"
            >
              Next
            </button>
          ) : (
            <button
              className="btn-primary"
              onClick={onGenerate}
              disabled={generating || !canProceed()}
              data-testid="wizard-generate"
            >
              {generating ? 'Generating...' : 'Generate Video'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
